import { all, call, put, select, takeEvery } from '@redux-saga/core/effects';
import { PayloadAction } from "@reduxjs/toolkit";
import { OpService } from "../../util/services/operation";
import { VesselSideService } from '../../util/services/vessel-side';
import { selectOneSetting, selectOverview, selectPermission, selectTenantId } from "../../util/store/selectors";
import { aWeekMillisecond, CONTRACT_PERMISSION_TYPE, CONTRACT_TYPE, OP_STATUS, OPERATION_TYPE } from "../../util/varibles/constants";
import { calculateTotalWeightAndTotalFish, cloneObj, jumpToTarget, showErrorResponse } from "../../util/varibles/global";
import { ILevelByVessel, IOperation, IOperation2, IOperations, IVessel, IWellboats, IWellboatsResponse, IWellboatVessel } from "../../util/varibles/interface";
import { VIEW_MODE } from '../../util/varibles/permission';
import { OverviewSetting } from "../../util/varibles/userSetting";
import { downLevel, generate, upLevel } from '../PlanOperationPage/util';
import { CALENDAR_ID, orderBySavedIds } from "./constants";
import { overviewActions } from "./reducer";

function* initialWatcher(args: PayloadAction<{ action?(vessels: IVessel[]): void }>): Generator<any, any, any> {
    try {
        const { action } = args.payload;
        const tenantId = yield select(selectTenantId);
        const weeks = yield select(selectOverview.weeks);
        const { time: start } = weeks[0];
        const { time: finish } = weeks[weeks.length - 1];
        const permission = yield select(selectPermission);
        const finishTime = finish + aWeekMillisecond - 1;
        const emptyWellboatsResponse: IWellboatsResponse = {
            operations: [],
            vessels: [],
            before_count: false,
            after_count: false,
        };
        const [result = {}, wellboatsResponse = emptyWellboatsResponse] = yield all([
            call(OpService.getScheduleOfWeeks, start, finishTime),
            permission[VIEW_MODE.VESSEL]
                ? call(OpService.getWellboatsForService, start, finishTime)
                : emptyWellboatsResponse,
        ]);
        const wellboats: IWellboats = {
            ...wellboatsResponse,
            vessels: wellboatsResponse.vessels
                .map((item: IWellboatVessel) => item.vessel),
            operations: wellboatsResponse.operations.reduce((items: IOperations, item: IOperation) => {
                items[item.operation.id] = item;
                return items;
            }, {}),
        };
        const vesselIds = yield select(selectOneSetting(OverviewSetting.VESSEL_FILTER));
        const vesselOrder = yield select(selectOneSetting(OverviewSetting.VESSEL_ORDER));
        let level: ILevelByVessel = {};
        let vessels: IVessel[] = result.vessels.reduce((items: any, item: any, index: number) => {
            const { vessel } = item
            const isOwn = `${tenantId}` === `${vessel.tenant_id}`;

            let permission = null;
            if (!isOwn) {
                const { contracts } = vessel;
                const contract = contracts.find((sub: any) => sub.tenant_id === tenantId
                    && sub.type === CONTRACT_TYPE.SINGLE
                    && sub.permission === CONTRACT_PERMISSION_TYPE.FULL)
                permission = contract ? contract.id : permission
            }

            return [...items, {
                ...vessel,
                isOwn,
                permission,
                row: 1,
                isShow: vesselIds.length === 0 || vesselIds.indexOf(vessel.id) !== -1,
                index,
            }];
        }, []);
        vessels = orderBySavedIds(vessels, vesselOrder)
            .map((vessel, index) => ({ ...vessel, index }));
        const operations = result.operations.reduce((list: any, item: any) => {
            const { id, operation_type } = item.operation;
            if (operation_type === OPERATION_TYPE.HARVEST)
                item = calculateTotalWeightAndTotalFish(item);
            const { vessel_id } = item.operation;
            const vessel = vessels.find(sub => sub.id === vessel_id);

            if (vessel) {
                list = { ...list, [id]: { ...item, vessel } };
                level = upLevel(level, list[id], list);
                const { row = 1 } = vessels[vessel.index] || {};
                vessels[vessel.index].row = Math.max(row, level[vessel_id][id].sub);
            }

            return list;
        }, {});


        yield put(overviewActions.initialDataSuccess({ result: { ...result, operations, vessels, wellboats, level }, tenantId, vesselIds }));
        if (action) {
            const callbackVessels = Array.from(new Map([
                ...result.vessels,
                ...wellboatsResponse.vessels,
            ].map(item => [item.vessel.id, item])).values());
            action(callbackVessels)
        }

        setTimeout(() => jumpToTarget(CALENDAR_ID), 300);
    } catch (error) {
        yield put(overviewActions.initialDataFailure())
        console.log(error);
    }
}

function* lazyLoadingWatcher({ ...args }): Generator<any, any, any> {
    try {
        const { isLoad, key, startTime, finishTime, weeks } = args.payload;
        if (isLoad) {
            const isBefore = key === "beforeCount";
            const oldWellboats: IWellboats = yield select(selectOverview.wellboats);
            const shouldLoadSchedule = yield select(isBefore
                ? selectOverview.beforeCount
                : selectOverview.afterCount);
            const shouldLoadWellboats = isBefore
                ? oldWellboats.before_count
                : oldWellboats.after_count;
            const emptyResult = {
                operations: [],
                vessels: [],
                before_count: false,
                after_count: false,
            };
            const [result, wellboatsResult] = yield all([
                shouldLoadSchedule
                    ? call(OpService.getScheduleOfWeeks, startTime, finishTime)
                    : emptyResult,
                shouldLoadWellboats
                    ? call(OpService.getWellboatsForService, startTime, finishTime)
                    : null,
            ]);
            const oldVessels: IVessel[] = yield select(selectOverview.vessels);
            const oldOperations = yield select(selectOverview.ops);
            let level = yield select(selectOverview.level);

            const vessels: IVessel[] = result.vessels.reduce((list: any, item: any) => {
                const isExist = list.some((sub: any) => sub.id === item.vessel.id)

                if (!isExist)
                    list.push({ ...item.vessel, index: list.length })

                return list;
            }, oldVessels.map(vessel => ({ ...vessel })));

            const operations = result.operations.reduce((list: any, item: any) => {
                const { id, operation_type } = item.operation;
                level = downLevel(level, list[id], list);
                if (operation_type === OPERATION_TYPE.HARVEST)
                    item = calculateTotalWeightAndTotalFish(item);
                const { vessel_id } = item.operation;
                const vessel = vessels.find(sub => sub.id === vessel_id);

                if (vessel) {
                    list = { ...list, [id]: { ...item, vessel } };
                    level = upLevel(level, list[id], list);
                    const { row = 1 } = vessels[vessel.index] || {};
                    vessels[vessel.index].row = Math.max(row, level[vessel_id][id].sub);
                }
                return list;
            }, oldOperations);
            const wellboats: IWellboats = wellboatsResult
                ? {
                    operations: wellboatsResult.operations.reduce(
                        (items: IOperations, item: IOperation) => {
                            items[item.operation.id] = item;
                            return items;
                        },
                        Object.values(oldWellboats.operations).reduce((items: IOperations, item) => {
                            items[item.operation.id] = item;
                            return items;
                        }, {}),
                    ),
                    vessels: Array.from(new Map([
                        ...oldWellboats.vessels,
                        ...wellboatsResult.vessels.map((item: IWellboatVessel) => item.vessel),
                    ].map(item => [item.id, item])).values()),
                    before_count: isBefore
                        ? wellboatsResult.before_count
                        : oldWellboats.before_count,
                    after_count: isBefore
                        ? oldWellboats.after_count
                        : wellboatsResult.after_count,
                }
                : oldWellboats;
            yield put(overviewActions.lazyLoadingSuccess({
                result: { ...result, vessels, operations, wellboats, level },
                key,
                weeks,
            }))
        }
    } catch (error) {
        console.log(error);
        yield put(overviewActions.lazyLoadingFailure())
    }
}

function* addOpsWatcher(
    args: ReturnType<typeof overviewActions.addOps>
): Generator<any, void, any> {
    try {
        const list = args.payload;
        const vessels = yield select(selectOverview.vessels);
        const operations = yield select(selectOverview.ops);
        const wellboats: IWellboats = yield select(selectOverview.wellboats);
        const wellboatVesselIds = new Set(wellboats.vessels.map(item => item.id));
        let level = yield select(selectOverview.level);

        const ops = list.reduce((rs: any, item) => {
            const { id, operation_type } = item.operation;
            if (operation_type === OPERATION_TYPE.HARVEST)
                item = calculateTotalWeightAndTotalFish(item);
            const { vessel_id } = item.operation;
            const vessel = item.vessel || vessels.find((sub: any) => sub.id === vessel_id);
            rs = { ...rs, [id]: { ...item, vessel } };
            level = upLevel(level, rs[id], rs);
            return rs;
        }, operations);
        yield put(overviewActions.updateOperations({ ops, level }))

        const wellboatOperations = list.reduce((rs: IOperations, item) => {
            const { id, vessel_id } = item.operation;
            if (!vessel_id || !wellboatVesselIds.has(vessel_id))
                return rs;

            const vessel = item.vessel || wellboats.vessels.find(sub => sub.id === vessel_id);
            rs[id] = { ...item, vessel };
            return rs;
        }, cloneObj(wellboats.operations));
        yield put(overviewActions.updateWellboatOperations(wellboatOperations));
    } catch (error) {
        console.log(error);
    }

}

function* updateOpsWatcher(args: ReturnType<typeof overviewActions.updateOps>): Generator<any, void, any> {
    try {
        const list = args.payload || [];
        const operations = yield select(selectOverview.ops);
        const wellboats: IWellboats = yield select(selectOverview.wellboats);
        const wellboatVesselIds = new Set(wellboats.vessels.map(item => item.id));
        let level = yield select(selectOverview.level);
        const ops = list.reduce((rs: IOperations, item) => {
            const { id, status } = item.operation;
            level = downLevel(level, rs[id], rs);
            if (status === OP_STATUS.CANCELED) {
                delete rs[id];
                return rs;
            }
            if (!rs[id]) {
                rs = { ...rs, [id]: item };
            } else {
                rs = {
                    ...rs,
                    [id]: {
                        ...rs[id],
                        ...item,
                        operation: { ...rs[id].operation, ...item.operation },
                    }
                };
            }
            level = upLevel(level, rs[id], rs);
            return rs;
        }, cloneObj(operations))
        yield put(overviewActions.updateOperations({ ops, level }))

        const wellboatOperations = list.reduce((rs: IOperations, item) => {
            const { id, status, vessel_id } = item.operation;
            if (!rs[id] && (!vessel_id || !wellboatVesselIds.has(vessel_id)))
                return rs;
            if (status === OP_STATUS.CANCELED) {
                delete rs[id];
                return rs;
            }

            const vessel = item.vessel || wellboats.vessels.find(sub => sub.id === vessel_id);
            rs[id] = rs[id]
                ? {
                    ...rs[id],
                    ...item,
                    operation: { ...rs[id].operation, ...item.operation },
                    vessel: vessel || rs[id].vessel,
                }
                : { ...item, vessel };
            return rs;
        }, cloneObj(wellboats.operations));
        yield put(overviewActions.updateWellboatOperations(wellboatOperations));
    } catch (error) {
        console.log(error);
    }
}

function* deleteOpsWatcher(args: ReturnType<typeof overviewActions.deleteOps>): Generator<any, void, any> {
    try {
        const ids = new Set(args.payload);
        const operations = yield select(selectOverview.ops);
        const wellboats: IWellboats = yield select(selectOverview.wellboats);
        let level = yield select(selectOverview.level);
        const ops = Object.keys(operations).reduce((list: any, key: any) => {
            if (!ids.has(key))
                return { ...list, [key]: operations[key] };
            else {
                level = downLevel(level, operations[key], operations);
            }
            return list;
        }, {});
        yield put(overviewActions.updateOperations({ ops, level }))

        const wellboatOperations = Object.keys(wellboats.operations).reduce((list: IOperations, key) => {
            if (!ids.has(key))
                list[key] = wellboats.operations[key];
            return list;
        }, {});
        yield put(overviewActions.updateWellboatOperations(wellboatOperations));
    } catch (error) {
        console.log(error);
    }
}

function* saveOpWatcher(action: PayloadAction<{ data: IOperation2, success?: (value: any) => void, failure?: (value: any) => void }>): Generator<any, void, any> {
    const { data, success, failure } = action.payload;
    try {
        const vessels = [
            {
                vessel_id: data.vessel.id,
                vessel_owner_id: data.vessel.tenant_id,
                operations: [generate(data, { status: data.operation.status })]
            }
        ]

        const result = yield call(VesselSideService.crewSend, { vessels })
        success?.(result)
    } catch (error) {
        failure?.(error);
        showErrorResponse('Update failed', error)
    }

}

export default function* overviewSaga() {
    yield all([
        takeEvery(overviewActions.initialDataRequest.type, initialWatcher),
        takeEvery(overviewActions.lazyLoadingRequest.type, lazyLoadingWatcher),
        takeEvery(overviewActions.addOps.type, addOpsWatcher),
        takeEvery(overviewActions.updateOps.type, updateOpsWatcher),
        takeEvery(overviewActions.deleteOps.type, deleteOpsWatcher),
        takeEvery(overviewActions.saveOp.type, saveOpWatcher),
    ])
}
